import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import {
  getClub,
  createClub,
  editClub,
  deactivateClub,
  reactivateClub,
  getClubCourses,
  transferClubProviderMapping,
  generateInitialMapping,
  updateClubProviderMapping,
  unmapClubProviderMapping,
} from './thunks'
import { reactivateCourse, deactivateCourse, duplicateCourse, transferCourse } from 'store/course/thunks'

const club: Club = {
  id: 0,
  name: '',
  country: {
    id: 0,
    name: '',
  },
  courses: [],
}

const initialState: ClubState = {
  club: club,
  status: 'idle',
  error: null,
  saveStatus: 'idle',
  saveError: null,
  coursesStatus: 'idle',
  coursesError: null,
  initialMappingStatus: 'idle',
  initialMappingError: null,
  updateClubProviderMappingStatus: 'idle',
  updateClubProviderMappingError: null,
  unmapClubProviderMappingStatus: 'idle',
  unmapClubProviderMappingError: null,
}

const slice = createSlice({
  name: 'clubs',
  initialState,
  reducers: {
    clearClub: (state) => {
      state.club = club
      state.status = 'idle'
      state.error = null
      state.saveStatus = 'idle'
      state.saveError = null
      state.coursesStatus = 'idle'
      state.coursesError = null
      state.initialMappingStatus = 'idle'
      state.initialMappingError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClub.pending, (state) => {
        state.status = 'loading'
        state.error = null
        state.club = club
      })
      .addCase(getClub.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getClub.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const courses = state.club.courses
        state.club = { ...state.club, ...action.payload, courses }
      })

    builder
      .addCase(createClub.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(createClub.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(createClub.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        state.club = action.payload
      })

    builder
      .addCase(editClub.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(editClub.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(editClub.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        const courses = state.club.courses
        state.club = action.payload
        state.club.courses = courses
      })

    builder
      .addCase(deactivateClub.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(deactivateClub.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deactivateClub.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        const courses = state.club.courses
        state.club = action.payload
        state.club.courses = courses
      })

    builder
      .addCase(reactivateClub.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(reactivateClub.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(reactivateClub.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        const courses = state.club.courses
        state.club = action.payload
        state.club.courses = courses
      })

    builder
      .addCase(transferClubProviderMapping.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(transferClubProviderMapping.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(transferClubProviderMapping.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        const courses = state.club.courses
        state.club = action.payload
        state.club.courses = courses
      })

    builder
      .addCase(getClubCourses.pending, (state) => {
        state.coursesStatus = 'loading'
        state.coursesError = null
      })
      .addCase(getClubCourses.rejected, (state, action) => {
        state.coursesStatus = 'failed'
        state.coursesError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getClubCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'succeeded'
        state.club.courses = action.payload
      })

    builder
      .addCase(generateInitialMapping.pending, (state) => {
        state.initialMappingStatus = 'loading'
        state.initialMappingError = null
      })
      .addCase(generateInitialMapping.rejected, (state, action) => {
        state.initialMappingStatus = 'failed'
        state.initialMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(generateInitialMapping.fulfilled, (state, action) => {
        state.initialMappingStatus = 'succeeded'
        state.club = action.payload
      })

    builder.addCase(reactivateCourse.fulfilled, (state, action) => {
      const updatedId = action.payload.id
      const currentIndex = state.club.courses.map((course: Course) => course.id).indexOf(updatedId)
      if (currentIndex >= 0) {
        state.club.courses[currentIndex] = action.payload
      }
    })

    builder.addCase(deactivateCourse.fulfilled, (state, action) => {
      const updatedId = action.payload.id
      const currentIndex = state.club.courses.map((course: Course) => course.id).indexOf(updatedId)
      if (currentIndex >= 0) {
        state.club.courses[currentIndex] = action.payload
      }
    })

    builder.addCase(duplicateCourse.fulfilled, (state, action) => {
      if (state.club.courses.length > 0 && state.club.id === action.payload.club?.id) {
        state.club.courses.unshift(action.payload)
      }
    })

    builder.addCase(transferCourse.fulfilled, (state, action) => {
      if (state.club.courses.length > 0 && state.club.id === action.payload.club?.id) {
        state.club.courses.unshift(action.payload)
      }
    })

    builder
      .addCase(updateClubProviderMapping.pending, (state) => {
        state.updateClubProviderMappingStatus = 'loading'
        state.updateClubProviderMappingError = null
      })
      .addCase(updateClubProviderMapping.rejected, (state, action) => {
        state.updateClubProviderMappingStatus = 'failed'
        state.updateClubProviderMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateClubProviderMapping.fulfilled, (state, action) => {
        state.updateClubProviderMappingStatus = 'succeeded'
        state.club = { ...state.club, ...action.payload }
      })

    builder
      .addCase(unmapClubProviderMapping.pending, (state) => {
        state.unmapClubProviderMappingStatus = 'loading'
        state.unmapClubProviderMappingError = null
      })
      .addCase(unmapClubProviderMapping.rejected, (state, action) => {
        state.unmapClubProviderMappingStatus = 'failed'
        state.unmapClubProviderMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(unmapClubProviderMapping.fulfilled, (state, action) => {
        state.unmapClubProviderMappingStatus = 'succeeded'
        state.club = { ...state.club, ...action.payload }
      })
  },
})

export const { clearClub } = slice.actions

export const selectClub = (state: RootState): ClubState => state.club

export default slice.reducer
